import { Files, VisualizerProject } from "@app/shared";
import cn from "classnames";
import { useCallback, useState } from "react";
import { emptyGuid, ticksToDate } from "../../../../helpers";
import { DateInput, InputRow, MediaSelector, NumericInput, Select, SelectOption } from "../../../shared";

type EditProjectFilesProps = {
    project: Partial<VisualizerProject>;
    setProject: React.Dispatch<React.SetStateAction<Partial<VisualizerProject>>>;
    maxHeight?: number;
};

export const EditProjectFiles: React.FC<EditProjectFilesProps> = ({ project, setProject, ...props }) => {


    const [fileOptions, setFileOptions] = useState<SelectOption[]>([]);

    const handleFilesChanged = useCallback((files: string[] | undefined) => {
        console.log("HandleFilesChanged");
        setFileOptions((files ?? []).map(f => ({ label: f, value: f } as SelectOption)));
        //If we have a mkv file for the video, see if we can find a reasonable tick to use for the start date
        const videoFile = files?.find(f => f.toLowerCase().endsWith(".mkv") || f.toLowerCase().endsWith(".mp4"));
        const radarBinFile = files?.find(f => (f.toLowerCase().endsWith(".radar") || f.toLowerCase().endsWith(".bin")) && f.toLowerCase().includes("radar"));
        const inferrenceFile = files?.find(f => f.toLowerCase().endsWith(".json") && f.toLowerCase().includes("mkv"));
        const deviceSettingsFile = files?.find(f => f.toLowerCase().endsWith(".json") && f.toLowerCase().includes("devicesettings"));
        const simResultFile = files?.find(f => f.toLowerCase().endsWith(".json") && (f.toLowerCase().includes("results") || f.toLowerCase().includes("output")) && !f.toLowerCase().includes("user"));
        const userResultFile = files?.find(f => f.toLowerCase().endsWith(".json") && (f.toLowerCase().includes("results") || f.toLowerCase().includes("output")) && f.toLowerCase().includes("user"));

        const videoFileTicks = parseInt(videoFile?.slice(0, 18) ?? "");
        const radarFileTicks = parseInt(radarBinFile?.slice(0, 18) ?? "");

        if (videoFileTicks || radarFileTicks) {
            setProject((old) => ({ ...old, videoStartDate: ticksToDate(videoFileTicks ?? radarFileTicks) }));
            setProject((old) => ({ ...old, radarStartDate: ticksToDate(radarFileTicks ?? videoFileTicks) }));
        }
        if (radarBinFile && !project.radarDataBinaryFile) {
            setProject((old) => ({ ...old, radarDataBinaryFile: radarBinFile }));
        }
        if (simResultFile && !project.simResultsJsonFile) {
            setProject((old) => ({ ...old, simResultsJsonFile: simResultFile }));
        }
        if (userResultFile && !project.userResultsJsonFile) {
            setProject((old) => ({ ...old, userResultsJsonFile: userResultFile }));
        }
        if (videoFile && !project.videoFile) {
            setProject((old) => ({ ...old, videoFile: videoFile }));
        }
        if (inferrenceFile && !project.inferrenceJsonFile) {
            setProject((old) => ({ ...old, inferrenceJsonFile: inferrenceFile }));
        }
        if (deviceSettingsFile && !project.deviceSettingsJsonFile) {
            setProject((old) => ({ ...old, deviceSettingsJsonFile: deviceSettingsFile }));
        }
    }, [setFileOptions]);

    if ((project.id + "") === emptyGuid) {
        return <div>Invalid Project ID !</div>
    }
    return (
        <div className={cn(props.maxHeight ? `max-h-[${props.maxHeight}px]` : "", "overflow-y-auto")}>
            <InputRow>
                <DateInput label="Video Start Date" dateFormat="MM/dd/yyyy hh:mm:ss" onChange={(val) => setProject((old) => ({ ...old, videoStartDate: val ?? undefined }))} value={project.videoStartDate?.toISOString()} ></DateInput>
            </InputRow>
            <InputRow>
                <DateInput label="Radar Start Date" dateFormat="MM/dd/yyyy hh:mm:ss" onChange={(val) => setProject((old) => ({ ...old, radarStartDate: val ?? undefined }))} value={project.radarStartDate?.toISOString()} ></DateInput>
            </InputRow>
            <InputRow>
                <NumericInput label="Video FPS" value={project.videoFps} onChange={(val) => setProject((old) => ({ ...old, videoFps: val }))} />
            </InputRow>
            <InputRow>
                <Select options={fileOptions} emptyOptionDisabled={false} emptyOptionText="None" label="Video Data File" value={project.videoFile} onChange={(val) => setProject((old) => ({ ...old, videoFile: val.target.value }))} />
            </InputRow>
            <InputRow>
                <Select options={fileOptions} emptyOptionDisabled={false} emptyOptionText="None" label="Binary Radar Data File" value={project.radarDataBinaryFile} onChange={(val) => setProject((old) => ({ ...old, radarDataBinaryFile: val.target.value }))} />
            </InputRow>
            <InputRow>
                <Select options={fileOptions} emptyOptionDisabled={false} emptyOptionText="None" label="Inferrence Overlay File" value={project.inferrenceJsonFile} onChange={(val) => setProject((old) => ({ ...old, inferrenceJsonFile: val.target.value }))} />
            </InputRow>
            <InputRow>
                <Select options={fileOptions} emptyOptionDisabled={false} emptyOptionText="None" label="Device Settings File" value={project.deviceSettingsJsonFile} onChange={(val) => setProject((old) => ({ ...old, deviceSettingsJsonFile: val.target.value }))} />
            </InputRow>
            <InputRow>
                <Select options={fileOptions} emptyOptionDisabled={false} emptyOptionText="None" label="Sim Results File" value={project.simResultsJsonFile} onChange={(val) => setProject((old) => ({ ...old, simResultsJsonFile: val.target.value }))} />
            </InputRow>
            <InputRow>
                <Select options={fileOptions} emptyOptionDisabled={false} emptyOptionText="None" label="User Results File" value={project.userResultsJsonFile} onChange={(val) => setProject((old) => ({ ...old, userResultsJsonFile: val.target.value }))} />
            </InputRow>
            <InputRow>
                <div>Project Files:</div>
                <MediaSelector
                    listFormat={true}
                    path={"visualizer/" + project.id}
                    allowMultiple={true}
                    fileDescription="File"
                    fileTypes=".mkv,.json,.mp4,.radar,.bin"
                    value={undefined}
                    onChange={async (file) => {
                        console.log("Downloading file " + file);
                        //Download this file
                        const fileUrl = await Files.getPublicUrl("visualizer/" + project.id + "/" + file);
                        if (!fileUrl) {
                            console.error("Failed to get file URL");
                            return;
                        }
                        // Create a temporary anchor element
                        const link = document.createElement("a");
                        link.href = fileUrl;
                        link.download = file; // Set the filename for the downloaded file
                        link.target = "_blank";
                        document.body.appendChild(link);

                        // Programmatically click the link to trigger the download
                        link.click();

                        console.log(link);

                        // Clean up the temporary element
                        document.body.removeChild(link);
                    }}
                    onFilesChanged={handleFilesChanged}
                />
            </InputRow>
        </div>
    );
}